import {
  CheckCircleOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  LoadingOutlined
} from '@ant-design/icons'
import { Button, Form, Input } from 'antd'
import FormItem from 'components/FormItem'
import { useStoreActions, useStoreState } from 'easy-peasy'
import PropTypes from 'prop-types'
import { useState } from 'react'
import styled from 'styled-components'
import { StyledAuthWrapper, StyledFormWrapper } from '../styles'

const Title = styled.h2`
  color: #8b4513;
  font-size: 48px;
  margin-bottom: 2rem !important;
`

const Description = styled.p`
  color: #000;
  margin-bottom: 2rem !important;
`

const ValidationContainer = styled.div`
  margin-bottom: 1.5rem;
`

const ValidationItem = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
  color: ${(props) => (props.valid ? '#52c41a' : '#8c8c8c')};
`

export default function UpdatePW({
  handleLogin,
  form,
  loginStateV2,
  forceResetOtpState,
  verifyEmailState,
  confirmOtp,
  setConfirmOtp,
  showPasswordUpdatedModal
}) {
  const [password, setPassword] = useState('')

  const actions = useStoreActions((a) => ({
    forceUpdatePassword: a.forceUpdatePassword,
    setForceResetOtpState: a.setForceResetOtpState,
    loginV2: a.loginV2
  }))
  const state = useStoreState((s) => ({
    forceUpdatePasswordState: s.forceUpdatePasswordState,
    loginStateV2: s.loginStateV2
  }))

  const hasNoSpace = !password.includes(' ') && password.length > 0
  const hasMinLength = password.length >= 8
  const hasUppercase = /[A-Z]/.test(password)
  const hasLowercase = /[a-z]/.test(password)
  const hasDigit = /\d/.test(password)
  const hasSpecialCharacter = /[!@#$%^&*(),.?":{}|<>]/.test(password)

  const passwordValidation = [
    { valid: hasNoSpace, text: 'Must have no space' },
    { valid: hasMinLength, text: 'Must be at least 8 characters in length' },
    { valid: hasUppercase, text: 'Must have at least 1 upper case character' },
    { valid: hasLowercase, text: 'Must have at least 1 lower case character' },
    { valid: hasDigit, text: 'Must have at least 1 digit/number' },
    {
      valid: hasSpecialCharacter,
      text: 'Must have at least 1 special character'
    }
  ]

  const handlePasswordChange = (e) => {
    setPassword(e.target.value)
    form.setFieldsValue({ password: e.target.value })
  }

  const handleSubmit = () => {
    actions.forceUpdatePassword({
      email: form.getFieldValue('username'),
      password: password,
      confirm_otp: confirmOtp
    })
  }

  return (
    <StyledAuthWrapper justify="center" align="middle">
      <StyledFormWrapper>
        <img
          className="mr-peri"
          alt="MR. PERI"
          draggable="false"
          src="/images/auth-assets/mr-peri.png"
        />

        <Title>
          Secure Your Account <br /> with a New Password
        </Title>
        <Description>
          We take your privacy and security seriously. Set a strong, new
          password to help keep your Peri-Peri account safe.
        </Description>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ remember: true }}
          style={{ width: 400 }}
        >
          <FormItem
            form={form}
            name="password"
            label="New Password"
            style={{ height: 'auto' }}
            disabled={
              state.forceUpdatePasswordState.status === 1 ||
              state.loginStateV2.status === 1
            }
            rules={[
              { required: true, message: 'Please input your password!' },
              { min: 8, message: 'Password must be at least 8 characters!' },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve()

                  if (value.includes(' ')) {
                    return Promise.reject(
                      new Error('Password cannot contain spaces!')
                    )
                  }

                  if (!/[A-Z]/.test(value)) {
                    return Promise.reject(
                      new Error(
                        'Password must contain at least 1 uppercase letter!'
                      )
                    )
                  }

                  if (!/[a-z]/.test(value)) {
                    return Promise.reject(
                      new Error(
                        'Password must contain at least 1 lowercase letter!'
                      )
                    )
                  }

                  if (!/\d/.test(value)) {
                    return Promise.reject(
                      new Error('Password must contain at least 1 number!')
                    )
                  }

                  if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(value)) {
                    return Promise.reject(
                      new Error(
                        'Password must contain at least 1 special character!'
                      )
                    )
                  }

                  return Promise.resolve()
                }
              }
            ]}
          >
            {(props) => (
              <Input.Password
                {...props}
                disabled={
                  state.forceUpdatePasswordState.status === 1 ||
                  state.loginStateV2.status === 1
                }
                onChange={(e) => {
                  handlePasswordChange(e)
                }}
                iconRender={(visible) =>
                  visible ? <EyeOutlined /> : <EyeInvisibleOutlined />
                }
              />
            )}
          </FormItem>

          <ValidationContainer>
            {passwordValidation.map((item, index) => (
              <ValidationItem key={index} valid={item.valid}>
                <CheckCircleOutlined
                  style={{
                    color: item.valid ? '#52c41a' : '#8c8c8c',
                    marginRight: 10
                  }}
                />
                {item.text}
              </ValidationItem>
            ))}
          </ValidationContainer>

          <FormItem
            form={form}
            name="confirm_password"
            label="Confirm New Password"
            disabled={
              state.forceUpdatePasswordState.status === 1 ||
              state.loginStateV2.status === 1
            }
            rules={[
              { required: true, message: 'Please confirm your password!' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve()
                  }
                  return Promise.reject(
                    new Error('The two passwords do not match!')
                  )
                }
              })
            ]}
          >
            {(props) => (
              <Input.Password
                {...props}
                disabled={
                  state.forceUpdatePasswordState.status === 1 ||
                  state.loginStateV2.status === 1
                }
                iconRender={(visible) =>
                  visible ? <EyeOutlined /> : <EyeInvisibleOutlined />
                }
              />
            )}
          </FormItem>

          <Button
            disabled={
              state.forceUpdatePasswordState.status === 1 ||
              state.loginStateV2.status === 1
            }
            style={{ width: '100%' }}
            type="primary"
            htmlType="submit"
          >
            {state.forceUpdatePasswordState.status === 1 ||
            state.loginStateV2.status === 1
              ? (
                <LoadingOutlined />
                )
              : (
                  'Update Password'
                )}
          </Button>
        </Form>
      </StyledFormWrapper>
    </StyledAuthWrapper>
  )
}

UpdatePW.propTypes = {
  handleLogin: PropTypes.func,
  form: PropTypes.object.isRequired,
  loginStateV2: PropTypes.shape({
    status: PropTypes.number,
    payload: PropTypes.any
  }),
  forceResetOtpState: PropTypes.shape({
    status: PropTypes.number,
    payload: PropTypes.any
  }),
  verifyEmailState: PropTypes.shape({
    status: PropTypes.number,
    payload: PropTypes.any
  }),
  confirmOtp: PropTypes.string,
  setConfirmOtp: PropTypes.func,
  showPasswordUpdatedModal: PropTypes.func
}
